import { Avatar, Button, Chip, Input, Select } from '@sjuni/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, ShieldCheck, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { FormError } from '@/components/FormError';
import { type Column, ResourceListScreen } from '@/components/list/ResourceListScreen';
import { ApiRequestError, apiFetch } from '@/lib/api/client';
import { useResourceList } from '@/lib/api/resource';
import { useAuth } from '@/lib/auth/useAuth';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import { useListParams } from '@/lib/list/useListParams';

import { ADMIN_LOCALES, type RoleRow, type UserRow } from './types';

interface FacultyRow {
  id: number;
  name: Record<string, string | null>;
  deleted_at: string | null;
}

/**
 * docs/05-ADMIN-DASHBOARD.md §5.10 — `/users`.
 *
 * "User list with role badges, faculty scope, last login, and active status.
 * Invite-by-email flow."
 *
 * The editor is a drawer over the list, at the URL §2 names. Both: `/users/12`
 * is a place an administrator can be sent to, and the work here is comparing
 * one account against the others — who else is a Faculty Admin, who has never
 * signed in — which a full-page editor hides behind a navigation each way.
 */
export function UsersPage() {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const list = useListParams('name');

  // `/users/new` and `/users/12`, read off the path the module route
  // (`users/*`) hands through. The drawer is the screen for both.
  const segment = location.pathname.replace(/^\/users\/?/, '').replace(/\/$/, '');
  const editing = segment === '' ? null : segment;

  const close = () => navigate('/users');

  const roles = useQuery({
    queryKey: ['admin', 'roles'],
    queryFn: () => apiFetch<{ data: RoleRow[] }>('/admin/roles'),
  });

  const columns: Column<UserRow>[] = [
    {
      key: 'name',
      label: t('users.columns.person'),
      sortable: true,
      render: (row) => (
        <div className="flex min-w-52 items-center gap-3">
          {/*
            `Avatar` falls back to initials, so a team that never uploads a
            photograph still gets a consistent row rather than a gap.
          */}
          <Avatar
            src={row.avatar?.url ?? null}
            initials={initials(row.name)}
            size="sm"
            // The name is right beside it; describing the picture as well
            // would announce the person twice (§7.4).
            alt=""
          />

          <div className="min-w-0">
            <span className="font-semibold text-primary">{row.name}</span>
            <span className="mt-0.5 block truncate text-caption text-muted">{row.email}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'roles',
      label: t('users.columns.roles'),
      render: (row) => (
        <span className="flex flex-wrap gap-1">
          {row.roles.map((role) => (
            <Chip key={role} tone={role === 'super-admin' ? 'gold' : 'neutral'}>
              {t(`roles.${role}`, role)}
            </Chip>
          ))}
          {/* §3 — "A Faculty Admin has `users.faculty_id` set", and the scope
              is the whole meaning of the role, so it is shown beside it. */}
          {row.faculty ? (
            <Chip tone="navy">{row.faculty.name[locale] ?? row.faculty.name.ka ?? ''}</Chip>
          ) : null}
        </span>
      ),
    },
    {
      key: 'last_login_at',
      label: t('users.columns.lastLogin'),
      sortable: true,
      render: (row) => (
        <span className="text-caption text-secondary">
          {row.last_login_at ? (
            new Date(row.last_login_at).toLocaleDateString(locale)
          ) : (
            // The invitation is still outstanding — the useful thing to know
            // before anyone asks why the account "does not work".
            <span className="text-muted">{t('users.neverSignedIn')}</span>
          )}
        </span>
      ),
    },
    {
      key: 'is_active',
      label: t('users.columns.status'),
      render: (row) => (
        <span className="flex flex-wrap items-center gap-1">
          <Chip tone={row.is_active ? 'navy' : 'neutral'}>
            {row.is_active ? t('users.active') : t('users.inactive')}
          </Chip>
          {/* §3 — a Super Admin without confirmed 2FA cannot get past the
              enrolment screen, so the list says so rather than leaving it to
              be discovered at the login form. */}
          {row.roles.includes('super-admin') && !row.two_factor_confirmed ? (
            <Chip tone="gold">{t('users.twoFactorMissing')}</Chip>
          ) : null}
        </span>
      ),
    },
  ];

  return (
    <>
      <ResourceListScreen
        resource="users"
        title={t('nav.items.users')}
        intro={t('users.intro')}
        columns={columns}
        labelFor={(row) => `${row.name} — ${row.email}`}
        defaultSort="name"
        emptyTitle={t('users.empty.title')}
        emptyBody={t('users.empty.body')}
        newLabel={t('users.new')}
        deleteTitle={t('users.delete.title')}
        deleteBody={(label) => t('users.delete.body', { label })}
        onOpen={(row) => navigate(`/users/${row.id}`)}
        filters={
          <Select
            label={t('users.columns.roles')}
            hideLabel
            // Same query string the list itself reads — `useListParams` is a
            // view onto the URL rather than state, so two callers agree.
            value={String(list.params.role ?? '')}
            onChange={(event) => list.setFilter('role', event.target.value || null)}
            options={[
              { value: '', label: t('users.allRoles') },
              ...(roles.data?.data ?? []).map((role) => ({
                value: role.name,
                label: t(`roles.${role.name}`, role.name),
              })),
            ]}
          />
        }
      />

      {editing !== null ? (
        <UserDrawer
          key={editing}
          id={editing === 'new' ? null : Number(editing)}
          roles={roles.data?.data ?? []}
          isSelf={(id) => me?.id === id}
          onClose={close}
        />
      ) : null}
    </>
  );
}

/** §5.10's editor. Five fields, one of which decides everything else. */
function UserDrawer({
  id,
  roles,
  isSelf,
  onClose,
}: {
  id: number | null;
  roles: RoleRow[];
  isSelf: (id: number) => boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { locale: uiLocale } = useEditorLocale();
  const queryClient = useQueryClient();

  const existing = useQuery({
    queryKey: ['admin', 'users', id],
    queryFn: () => apiFetch<{ data: UserRow }>(`/admin/users/${id}`),
    enabled: id !== null,
  });

  const faculties = useResourceList<FacultyRow>('faculties', { per_page: 100, sort: 'sort_order' });

  const row = existing.data?.data;

  const [draft, setDraft] = useState<{
    name: string;
    email: string;
    locale: string;
    is_active: boolean;
    roles: string[];
    faculty_id: string;
  } | null>(null);

  // Filled from the record the first time it lands, then owned by the form.
  const values = draft ?? {
    name: row?.name ?? '',
    email: row?.email ?? '',
    locale: row?.locale ?? 'ka',
    is_active: row?.is_active ?? true,
    roles: row?.roles ?? [],
    faculty_id: row?.faculty ? String(row.faculty.id) : '',
  };

  const set = (changes: Partial<typeof values>) => setDraft({ ...values, ...changes });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const needsFaculty = values.roles.includes('faculty-admin');

  const save = useMutation({
    mutationFn: () =>
      apiFetch(id === null ? '/admin/users' : `/admin/users/${id}`, {
        method: id === null ? 'POST' : 'PUT',
        body: {
          name: values.name.trim(),
          email: values.email.trim(),
          locale: values.locale,
          is_active: values.is_active,
          roles: values.roles,
          faculty_id: needsFaculty && values.faculty_id !== '' ? Number(values.faculty_id) : null,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      onClose();
    },
    onError: (cause: unknown) => {
      if (cause instanceof ApiRequestError) {
        setErrors(
          Object.fromEntries(
            Object.entries(cause.validationErrors).map(([field, messages]) => [
              field,
              messages[0] ?? '',
            ]),
          ),
        );
      }

      setError(cause instanceof ApiRequestError ? cause.message : t('errors.generic'));
    },
  });

  const invite = useMutation({
    mutationFn: () => apiFetch(`/admin/users/${id}/invite`, { method: 'POST' }),
    onSuccess: () => setSent(true),
    onError: (cause: unknown) =>
      setError(cause instanceof ApiRequestError ? cause.message : t('errors.generic')),
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="user-heading"
      className="fixed inset-y-0 end-0 z-40 flex w-full max-w-xl flex-col overflow-y-auto border-s border-subtle bg-surface shadow-xl"
    >
      <div className="flex items-start justify-between gap-3 border-b border-subtle p-5">
        <h2 id="user-heading" className="text-h4 font-extrabold text-primary">
          {id === null ? t('users.new') : t('users.edit')}
        </h2>

        <Button
          type="button"
          size="sm"
          variant="ghost"
          aria-label={t('common.close')}
          onClick={onClose}
        >
          <X size={16} strokeWidth={1.75} aria-hidden="true" />
        </Button>
      </div>

      <form
        className="space-y-5 p-5"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          setErrors({});
          save.mutate();
        }}
      >
        <FormError message={error} />

        <Input
          label={t('users.fields.name')}
          value={values.name}
          onChange={(event) => set({ name: event.target.value })}
          error={errors.name}
        />

        <Input
          type="email"
          label={t('users.fields.email')}
          hint={t('users.fields.emailHint')}
          value={values.email}
          onChange={(event) => set({ email: event.target.value })}
          error={errors.email}
        />

        {/*
          §5.10's role editor is a set of checkboxes rather than a single
          select: spatie holds many roles per account, and the one real case is
          a Content Editor who also manages the vocational sub-site.
        */}
        <fieldset>
          <legend className="mb-1.5 text-body-sm font-semibold text-primary">
            {t('users.columns.roles')}
          </legend>

          {errors.roles ? (
            <p className="text-danger-600 mb-1.5 text-caption">{errors.roles}</p>
          ) : null}

          <div className="space-y-1.5">
            {roles.map((role) => (
              <label key={role.id} className="flex items-start gap-2 text-body-sm text-primary">
                <input
                  type="checkbox"
                  className="mt-0.5 size-4 rounded border-subtle text-navy-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
                  checked={values.roles.includes(role.name)}
                  onChange={(event) =>
                    set({
                      roles: event.target.checked
                        ? [...values.roles, role.name]
                        : values.roles.filter((name) => name !== role.name),
                    })
                  }
                />
                <span>
                  {t(`roles.${role.name}`, role.name)}
                  <span className="block text-caption text-muted">
                    {t(`users.roleHints.${role.name}`, '')}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        {/* Shown only for the role it means anything to — see §3's scoping. */}
        {needsFaculty ? (
          <Select
            label={t('users.fields.faculty')}
            hint={t('users.fields.facultyHint')}
            value={values.faculty_id}
            onChange={(event) => set({ faculty_id: event.target.value })}
            error={errors.faculty_id}
            options={[
              { value: '', label: t('common.choose') },
              ...(faculties.data?.data ?? []).map((faculty) => ({
                value: String(faculty.id),
                label: faculty.name[uiLocale] ?? faculty.name.ka ?? '',
              })),
            ]}
          />
        ) : null}

        <Select
          label={t('users.fields.locale')}
          hint={t('users.fields.localeHint')}
          value={values.locale}
          onChange={(event) => set({ locale: event.target.value })}
          error={errors.locale}
          options={ADMIN_LOCALES.map((option) => ({
            value: option,
            label: t(`users.locales.${option}`),
          }))}
        />

        <label className="flex items-start gap-2 text-body-sm text-primary">
          <input
            type="checkbox"
            className="mt-0.5 size-4 rounded border-subtle text-navy-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy-600"
            checked={values.is_active}
            // Refused by the server too — this only says so before the round
            // trip, and disabling it stops the box looking broken.
            disabled={id !== null && isSelf(id)}
            onChange={(event) => set({ is_active: event.target.checked })}
          />
          <span>
            {t('users.fields.isActive')}
            <span className="block text-caption text-muted">
              {id !== null && isSelf(id)
                ? t('users.fields.isActiveSelf')
                : t('users.fields.isActiveHint')}
            </span>
          </span>
        </label>

        {errors.is_active ? <FormError message={errors.is_active} /> : null}

        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? t('common.saving') : t('common.save')}
          </Button>

          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>

        {/* §5.10's invite-by-email, as the thing you reach for when someone
            says the link never arrived. */}
        {id !== null ? (
          <div className="space-y-2 border-t border-subtle pt-4">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              iconLeft={<KeyRound size={16} strokeWidth={1.75} aria-hidden="true" />}
              disabled={invite.isPending}
              onClick={() => invite.mutate()}
            >
              {t('users.resendInvitation')}
            </Button>

            <p className="text-caption text-muted">
              {sent ? t('users.invitationSent') : t('users.invitationHint')}
            </p>

            {row?.roles.includes('super-admin') ? (
              <p className="flex items-start gap-1.5 text-caption text-muted">
                <ShieldCheck
                  size={14}
                  strokeWidth={1.75}
                  aria-hidden="true"
                  className="mt-0.5 shrink-0"
                />
                {row.two_factor_confirmed
                  ? t('users.twoFactorOn')
                  : t('users.twoFactorMissingHint')}
              </p>
            ) : null}
          </div>
        ) : null}
      </form>
    </div>
  );
}

/**
 * Two letters for the avatar fallback.
 *
 * `Intl.Segmenter` rather than `slice`, for the same reason the header's copy
 * uses it: a Georgian letter is more than one UTF-16 unit, and slicing one in
 * half renders a replacement character.
 */
function initials(name: string): string {
  const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });

  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => [...segmenter.segment(part)][0]?.segment ?? '')
    .join('');
}
